import { useEffect, useState } from "react";
import { useSocketContext } from "@/context/socket.context/socket.context";

type EngagingPayload = {
  sessionName?: string;
  agentName?: string;
  engagementType?: "voice" | "video";
};
type DisengagedPayload = { sessionName?: string };

/**
 * Tracks leads that ANOTHER agent is currently calling (voice flow).
 *
 * When agent A clicks "Call Lead", their portal emits `agent:engaging`
 * (engagementType "voice"), which the backend relays to every other agent in
 * the company room. This hook records those engagements keyed by sessionName so
 * the leads widget can lock that specific lead's CTAs and show who is already
 * on it — preventing two agents from calling the same lead at once. Entries are
 * cleared on `agent:disengaged` (the call ended), which the backend relays the
 * same way.
 *
 * Scoped to voice on purpose: video engagements already flip the session to
 * "in progress" (the card then shows Rejoin), so they don't need this lock.
 *
 * @returns map of sessionName -> engaging agent's name
 */
export function useOtherAgentEngagements(): Record<string, string> {
  const socket = useSocketContext();
  const [engagements, setEngagements] = useState<Record<string, string>>({});

  useEffect(() => {
    const onEngaging = ({ sessionName, agentName, engagementType }: EngagingPayload) => {
      if (!sessionName || engagementType !== "voice") return;
      setEngagements((prev) => ({ ...prev, [sessionName]: agentName || "Another agent" }));
    };

    const onDisengaged = ({ sessionName }: DisengagedPayload) => {
      if (!sessionName) return;
      setEngagements((prev) => {
        if (!(sessionName in prev)) return prev;
        const next = { ...prev };
        delete next[sessionName];
        return next;
      });
    };

    socket.on("agent:engaging", onEngaging);
    socket.on("agent:disengaged", onDisengaged);

    return () => {
      socket.off("agent:engaging", onEngaging);
      socket.off("agent:disengaged", onDisengaged);
    };
  }, [socket]);

  return engagements;
}
